import React, {useState} from "react";
import {useForm} from 'react-hook-form'
import {NavLink, useLocation} from "react-router-dom";
import {
    Box,
    Button,
    Flex,
    FormControl, FormErrorMessage,
    FormLabel,
    Heading,
    Icon,
    Input,
    InputGroup,
    InputRightElement,
    Text,
    useColorModeValue,
} from "@chakra-ui/react";
import DefaultAuth from "layouts/auth/Default";
// Assets
import illustration from "assets/img/auth/landingpage-background.jpg";
import {MdOutlineRemoveRedEye} from "react-icons/md";
import {RiEyeCloseLine} from "react-icons/ri";
import {postData} from "../../../services/api";
import Footer from "../../../components/footer/FooterAuth";

function Reset() {
    // Chakra color mode
    const textColor = useColorModeValue("navy.700", "white");
    const textColorSecondary = "gray.400";
    const brandStars = useColorModeValue("brand.500", "brand.400");
    const [show, setShow] = useState(false);
    const [success, setSuccess] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const {
        handleSubmit,
        register,
        formState: {errors, isSubmitting},
        watch
    } = useForm()

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('token');
    const email = queryParams.get('email');

    function onSubmit(values) {
        return new Promise(async (resolve) => {
            try {
                values.token = token;
                values.email = email;
                await postData('/api/reset-password', values);
                setSuccess(true)
                resolve();
            } catch (error) {
                console.error('Fehler:', error);
            }
        })
    }

    const handleClick = () => setShow(!show);

    const handleClickConfirm = () => setShowConfirm(!showConfirm);

    const password = watch('password');

    return (
        <Box>
            <DefaultAuth illustrationBackground={illustration} image={illustration}>
                <Flex
                    maxW={{base: "100%", md: "40vw"}}
                    w='100%'
                    mx={{base: "auto", lg: "0px"}}
                    me='auto'
                    h='100%'
                    alignItems='start'
                    justifyContent='center'
                    mb={{base: "30px", md: "60px"}}
                    px={{base: "25px", md: "0px"}}
                    mt={{base: "40px", md: "14vh"}}
                    flexDirection='column'>
                    <Box me='auto'>
                        <Heading color={textColor} fontSize='36px' mb='10px'>
                            Neues Passwort
                        </Heading>
                        <Text
                            mb='36px'
                            ms='4px'
                            color={textColorSecondary}
                            fontWeight='400'
                            fontSize='md'
                        >
                            Vergib nun ein neues Passwort mit dem du dich einloggen möchtest.
                        </Text>
                    </Box>
                    {success ? (
                        <Box>
                            <Text
                                fontWeight='400'
                                fontSize='md'
                                w={{base: "100%", md: "420px"}}
                            >
                                Dein Passwort wurde erfolgreich angepasst! Du kannst dich nun mit dem neuen Passwort
                                anmelden.
                            </Text>
                            <NavLink to={'/auth/login'}>
                                <Button
                                    fontSize='sm'
                                    variant='brand'
                                    fontWeight='500'
                                    w='100%'
                                    h='50'
                                    mt='24px'
                                    mb='24px'
                                >
                                    Zum Login
                                </Button>
                            </NavLink>
                        </Box>
                    ) : (
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Flex
                                zIndex='2'
                                direction='column'
                                w={{base: '100%', md: '420px'}}
                                maxW='100%'
                                background='transparent'
                                borderRadius='15px'
                                mx={{base: 'auto', lg: 'unset'}}
                                me='auto'
                                mb={{base: '20px', md: 'auto'}}
                            >
                                {/* Password Field */}
                                <FormControl isInvalid={!!errors.password} mb='24px'>
                                    <FormLabel
                                        ms='4px'
                                        fontSize='sm'
                                        fontWeight='500'
                                        color={textColor}
                                        display='flex'
                                        htmlFor='password'
                                    >
                                        Passwort<Text color={brandStars}>*</Text>
                                    </FormLabel>
                                    <InputGroup size='md'>
                                        <Input
                                            id='password'
                                            fontSize='sm'
                                            placeholder='Min. 8 Zeichen'
                                            size='lg'
                                            type={show ? 'text' : 'password'}
                                            variant='auth'
                                            {...register('password', {
                                                required: 'Bitte gib ein Passwort an',
                                                minLength: {
                                                    value: 8,
                                                    message: 'Das Passwort muss mindestens 8 Zeichen lang sein',
                                                },
                                            })}
                                        />
                                        <InputRightElement display='flex' alignItems='center' mt='4px'>
                                            <Icon
                                                color={textColorSecondary}
                                                _hover={{cursor: 'pointer'}}
                                                as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                                                onClick={handleClick}
                                            />
                                        </InputRightElement>
                                    </InputGroup>
                                    <FormErrorMessage>
                                        {errors.password && errors.password.message}
                                    </FormErrorMessage>
                                </FormControl>

                                <FormControl isInvalid={!!errors.password_confirmation} mb='24px'>
                                    <FormLabel
                                        ms='4px'
                                        fontSize='sm'
                                        fontWeight='500'
                                        color={textColor}
                                        display='flex'
                                        htmlFor='password_confirmation'
                                    >
                                        Passwort bestätigen<Text color={brandStars}>*</Text>
                                    </FormLabel>
                                    <InputGroup size='md'>
                                        <Input
                                            id='password_confirmation'
                                            fontSize='sm'
                                            placeholder='Bestätige dein Passwort'
                                            size='lg'
                                            type={showConfirm ? 'text' : 'password'}
                                            variant='auth'
                                            {...register('password_confirmation', {
                                                required: 'Bitte bestätige dein Passwort',
                                                validate: value =>
                                                    value === password || 'Die Passwörter stimmen nicht überein',
                                            })}
                                        />
                                        <InputRightElement display='flex' alignItems='center' mt='4px'>
                                            <Icon
                                                color={textColorSecondary}
                                                _hover={{cursor: 'pointer'}}
                                                as={showConfirm ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                                                onClick={handleClickConfirm}
                                            />
                                        </InputRightElement>
                                    </InputGroup>
                                    <FormErrorMessage>
                                        {errors.password_confirmation && errors.password_confirmation.message}
                                    </FormErrorMessage>
                                </FormControl>

                                <Button
                                    fontSize='sm'
                                    variant='brand'
                                    fontWeight='500'
                                    w='100%'
                                    h='50'
                                    mt='24px'
                                    mb='24px'
                                    isLoading={isSubmitting}
                                    type='submit'
                                >
                                    Neues Passwort übernehmen
                                </Button>
                            </Flex>
                        </form>
                    )}
                </Flex>
            </DefaultAuth>

            <Footer/>
        </Box>
    );
}

export default Reset;
